import { HYDRATE } from 'next-redux-wrapper';
import { createSelector } from 'reselect';
import { ParsedUrlQuery } from 'querystring';
import { NextRouter } from 'next/router';

import { RootState, ThunkAction } from '@zoopla/redux-types';
import { updateResults } from '@zoopla/listing-search-update-results';
import {
    filtersValuesSelector,
    resetFilters
} from '@zoopla/redux-reducer-filters';
import {
    SearchPageType,
    SearchSource,
    UpdateTotalResultsAction,
    UpdatePageNumberAction,
    UpdatePageNumberMaxAction,
    UpdateTotalResultsWasLimitedAction,
    SearchResultsState,
    SearchResultsActionTypes,
    UpdatePolyencAction,
    ACTION_TYPES_SEARCH_RESULT
} from '@zoopla/redux-types';

// Actions
export const updateTotalResults = (
    totalResults: number
): UpdateTotalResultsAction => ({
    type: ACTION_TYPES_SEARCH_RESULT.UPDATE_TOTAL_RESULTS,
    totalResults
});
export const updatePageNumber = (
    pageNumber: number
): UpdatePageNumberAction => ({
    type: ACTION_TYPES_SEARCH_RESULT.UPDATE_PAGE_NUMBER,
    pageNumber
});
export const updatePageNumberMax = (
    pageNumberMax: number
): UpdatePageNumberMaxAction => ({
    type: ACTION_TYPES_SEARCH_RESULT.UPDATE_PAGE_NUMBER_MAX,
    pageNumberMax
});
export const updateTotalResultsWasLimited = (
    totalResultsWasLimited: boolean
): UpdateTotalResultsWasLimitedAction => ({
    type: ACTION_TYPES_SEARCH_RESULT.UPDATE_TOTAL_RESULTS_WAS_LIMITED,
    totalResultsWasLimited
});
export const updatePolyenc = (polyenc: string | null): UpdatePolyencAction => ({
    type: ACTION_TYPES_SEARCH_RESULT.UPDATE_POLYENC,
    polyenc
});

// Reducer
const initialState: SearchResultsState = {
    totalResults: 0,
    pageNumber: 0,
    pageNumberMax: 0,
    totalResultsWasLimited: false,
    polyenc: null
};

export const searchResults = (
    state = initialState,
    action: SearchResultsActionTypes
): SearchResultsState => {
    switch (action.type) {
        case HYDRATE: {
            return {
                ...state,
                ...action.payload.searchResults
            };
        }
        case ACTION_TYPES_SEARCH_RESULT.UPDATE_TOTAL_RESULTS: {
            const { totalResults } = action;

            return {
                ...state,
                totalResults
            };
        }

        case ACTION_TYPES_SEARCH_RESULT.UPDATE_PAGE_NUMBER_MAX: {
            const { pageNumberMax } = action;

            return {
                ...state,
                pageNumberMax
            };
        }

        case ACTION_TYPES_SEARCH_RESULT.UPDATE_PAGE_NUMBER: {
            const { pageNumber } = action;

            return {
                ...state,
                pageNumber
            };
        }
        case ACTION_TYPES_SEARCH_RESULT.UPDATE_TOTAL_RESULTS_WAS_LIMITED: {
            const { totalResultsWasLimited } = action;

            return {
                ...state,
                totalResultsWasLimited
            };
        }
        case ACTION_TYPES_SEARCH_RESULT.UPDATE_POLYENC: {
            const { polyenc } = action;
            return {
                ...state,
                polyenc
            };
        }
        default:
            return {
                ...state
            };
    }
};

// Selectors
const selectSearchResults = (state: RootState): SearchResultsState =>
    state.searchResults;
export const selectPageNumberMax = (state: RootState) =>
    selectSearchResults(state).pageNumberMax;
export const selectTotalResults = (state: RootState) =>
    selectSearchResults(state).totalResults;
export const selectPageNumber = (state: RootState) =>
    selectSearchResults(state).pageNumber;
export const selectTotalResultsWasLimited = (state: RootState) =>
    selectSearchResults(state).totalResultsWasLimited;
export const selectPolyenc = (state: RootState) =>
    selectSearchResults(state).polyenc;

export const totalPagesSelector = createSelector(
    selectPageNumberMax,
    (pageNumberMax) => {
        return pageNumberMax;
    }
);
export const totalResultsTextSelector = createSelector(
    selectTotalResults,
    selectTotalResultsWasLimited,
    (totalResults, totalResultsWasLimited) => {
        if (totalResultsWasLimited) {
            return `${totalResults}+ results`;
        }

        return `${totalResults} results`;
    }
);
export const polyencSelector = createSelector(
    selectPolyenc,
    (polyenc) => polyenc
);

// Action creators
export const resetResultsAsync =
    (
        query: ParsedUrlQuery,
        router: NextRouter,
        searchType: SearchPageType,
        searchSource: SearchSource,
        sortOrder: string,
        userAlertId: string | null = null
    ): ThunkAction =>
    async (dispatch, getState) => {
        await dispatch(resetFilters(undefined, query));

        const store = getState();
        const filters = filtersValuesSelector(store);

        updateResults(
            filters,
            router,
            searchType,
            searchSource,
            sortOrder,
            userAlertId,
            false,
            true
        );
    };

export default searchResults;
